"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const CRLF_RE = /\r\n?/g;
const MULTI_SPACE_RE = /[ \t]{2,}/g;
const LINE_EDGE_RE = /^[ \t]+|[ \t]+$/gm;
const MULTI_BLANK_RE = /\n{3,}/g;
const LINE_BREAK_RE = /[ \t]*\n+[ \t]*/g;

interface Options {
  collapseSpaces: boolean;
  trimLines: boolean;
  removeBlankLines: boolean;
  collapseBlankLines: boolean;
  removeLineBreaks: boolean;
}

const DEFAULT_OPTIONS: Options = {
  collapseSpaces: true,
  trimLines: true,
  removeBlankLines: false,
  collapseBlankLines: false,
  removeLineBreaks: false,
};

const OPTION_KEYS: (keyof Options)[] = [
  "collapseSpaces",
  "trimLines",
  "removeBlankLines",
  "collapseBlankLines",
  "removeLineBreaks",
];

function stripBlankLines(text: string): string {
  return text
    .split("\n")
    .filter((line) => line.trim().length > 0)
    .join("\n");
}

function cleanText(input: string, options: Options): string {
  let text = input.replace(CRLF_RE, "\n");
  if (options.collapseSpaces) {
    text = text.replace(MULTI_SPACE_RE, " ");
  }
  if (options.trimLines) {
    text = text.replace(LINE_EDGE_RE, "");
  }
  if (options.removeBlankLines) {
    text = stripBlankLines(text);
  }
  if (options.collapseBlankLines) {
    text = text.replace(MULTI_BLANK_RE, "\n\n");
  }
  if (options.removeLineBreaks) {
    text = text.replace(LINE_BREAK_RE, " ").trim();
  }
  return text;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const WHITESPACE_REMOVER_STRINGS = {
  yourText: "Your text",
  inputPlaceholder: "Paste text with messy spacing here...",
  cleanupOptions: "Cleanup options",
  optionLabels: {
    collapseSpaces: "Collapse multiple spaces/tabs to one",
    trimLines: "Trim line starts and ends",
    removeBlankLines: "Remove blank lines",
    collapseBlankLines: "Collapse multiple blank lines to one",
    removeLineBreaks: "Remove ALL line breaks (one paragraph)",
  },
  cleanText: "Clean Text",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  cleanedText: "Cleaned text",
  charactersBefore: "Characters before",
  charactersAfter: "Characters after",
  charactersRemoved: "Characters removed",
} as const;

export default function WhitespaceRemoverWidget() {
  const strings = useToolUi(WHITESPACE_REMOVER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [options, setOptions] = useState<Options>(DEFAULT_OPTIONS);
  const [output, setOutput] = useState("");
  const [cleaned, setCleaned] = useState(false);
  const [copied, setCopied] = useState(false);

  function toggleOption(key: keyof Options) {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleClean() {
    if (input.length === 0) {
      return;
    }
    setOutput(cleanText(input, options));
    setCleaned(true);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCleaned(false);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ws-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="ws-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.cleanupOptions}
        </p>
        <div className="flex flex-wrap gap-3">
          {OPTION_KEYS.map((key) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={key}
            >
              <input
                checked={options[key]}
                className="accent-primary"
                onChange={() => toggleOption(key)}
                type="checkbox"
              />
              {strings.optionLabels[key]}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClean}
          type="button"
        >
          {strings.cleanText}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {cleaned && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ws-output"
          >
            {strings.cleanedText}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="ws-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {cleaned && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.charactersBefore}
            value={fmt(input.length)}
          />
          <StatCard
            label={strings.charactersAfter}
            value={fmt(output.length)}
          />
          <StatCard
            label={strings.charactersRemoved}
            value={fmt(Math.max(0, input.length - output.length))}
          />
        </div>
      )}
    </>
  );
}
